import { Component, OnInit, OnDestroy, inject, ViewEncapsulation, HostListener } from '@angular/core';
import { RouterLink, Router, RouterLinkActive, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../../services/auth.service';
import { SearchService, SearchRequest } from '../../../services/search.service';
import { CallbackService } from '../../../services/callback.service';
import { LoadingComponent } from '../loading/loading.component';
import { Subscription, filter } from 'rxjs';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, CommonModule, FormsModule, LoadingComponent],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css',
  encapsulation: ViewEncapsulation.None
})
export class NavbarComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly searchService = inject(SearchService);
  private readonly callbackService = inject(CallbackService);

  isAuthenticated = false;
  userDisplayName = '';
  isMobileMenuOpen = false;
  isScrolled = false;
  isHomePage = false;
  isHotelDropdownOpen = false;
  isUserDropdownOpen = false;

  toggleUserDropdown(): void {
    this.isUserDropdownOpen = !this.isUserDropdownOpen;
  }

  closeUserDropdown(): void {
    this.isUserDropdownOpen = false;
  }

  getUserInitial(): string {
    return (this.userDisplayName?.trim().charAt(0) || 'U').toUpperCase();
  }
  mobileYurticiOpen = false;
  mobileYurtdisiOpen = false;
  navSearchLoading = false;

  callbackModalOpen = false;
  callbackName = '';
  callbackEmail = '';
  callbackPhone = '';
  callbackDate: 'Bugün' | 'Yarın' | 'Bu Hafta' | 'Farketmez' = 'Bugün';
  callbackTime: 'Farketmez' | 'Sabah (09-12)' | 'Öğlen (12-15)' | 'Öğleden Sonra (15-18)' | 'Akşam (18-21)' = 'Farketmez';
  callbackNote = '';
  callbackSubmitting = false;
  callbackSuccess = false;
  callbackError = '';

  openCallbackModal(): void {
    this.callbackModalOpen = true;
    this.callbackSuccess = false;
    this.callbackError = '';
    document.body.style.overflow = 'hidden';
  }

  closeCallbackModal(): void {
    this.callbackModalOpen = false;
    document.body.style.overflow = '';
  }

  submitCallback(): void {
    const name = this.callbackName.trim();
    const phone = this.callbackPhone.trim();
    if (!name || !phone || this.callbackSubmitting) return;
    this.callbackSubmitting = true;
    this.callbackError = '';
    this.callbackService.create({
      name,
      email: this.callbackEmail.trim() || undefined,
      phone,
      preferredDate: this.callbackDate,
      preferredTime: this.callbackTime,
      note: this.callbackNote.trim() || undefined
    }).subscribe({
      next: (res) => {
        this.callbackSubmitting = false;
        this.callbackSuccess = true;
        this.callbackName = '';
        this.callbackEmail = '';
        this.callbackPhone = '';
        this.callbackDate = 'Bugün';
        this.callbackTime = 'Farketmez';
        this.callbackNote = '';
      },
      error: (err) => {
        this.callbackSubmitting = false;
        this.callbackError = 'Talep gönderilemedi. Lütfen tekrar deneyin.';
      }
    });
  }
  private userSubscription?: Subscription;
  private routerSubscription?: Subscription;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isScrolled = window.scrollY > 50;
  }

  ngOnInit(): void {
    // Mevcut kullanıcı durumunu kontrol et
    this.updateUserStatus();
    
    // Kullanıcı durumu değişikliklerini dinle
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      this.updateUserStatus();
    });

    // Initial scroll check
    this.isScrolled = window.scrollY > 50;
    
    // Anasayfa kontrolü
    this.checkIfHomePage();
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkIfHomePage();
    });
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }
  
  private checkIfHomePage(): void {
    this.isHomePage = this.router.url === '/' || this.router.url === '';
  }

  private updateUserStatus(): void {
    this.isAuthenticated = this.authService.isAuthenticated();
    this.userDisplayName = this.authService.getUserDisplayName();
  }

  logout(): void {
    this.authService.logout();
  }

  navigateToBlog(event: Event): void {
    event.preventDefault();
    this.router.navigate(['/blog']);
    this.closeMobileMenu();
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    // Body scroll'u kontrol et
    if (this.isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    document.body.style.overflow = '';
  }

  toggleHotelDropdown(): void {
    this.isHotelDropdownOpen = !this.isHotelDropdownOpen;
  }

  closeHotelDropdown(): void {
    this.isHotelDropdownOpen = false;
  }

  toggleMobileSubmenu(menu: 'yurtici' | 'yurtdisi'): void {
    if (menu === 'yurtici') {
      this.mobileYurticiOpen = !this.mobileYurticiOpen;
    } else {
      this.mobileYurtdisiOpen = !this.mobileYurtdisiOpen;
    }
  }

  searchByNavItem(item: { destinationId: number; label: string; parentId: number; locationType: number }): void {
    this.closeHotelDropdown();
    this.closeMobileMenu();
    this.navSearchLoading = true;
    this.searchService.resetSearch();

    const searchRequest: SearchRequest = {
      currency: 'TRY',
      countryCode: 'TR',
      destinations: [{
        destinationId: item.destinationId,
        destinationLabel: item.label,
        destinationParentId: item.parentId,
        locationType: item.locationType
      }],
      pageNumber: 1,
      pageSize: 10,
      // ⚠️ Tarihsiz aramalarda API sadece 1 yolcu kabul ediyor
      rooms: [{ passengers: [{ age: 0, birthday: null, gender: null, passengerType: 0 }] }],
      includeRoomPrices: false,
    };

    this.searchService.setUserSearchCriteria({
      location: item.label,
      selectedLocation: { label: item.label, value: item.destinationId, type: item.locationType === 6 ? 'category' : 'destination', parentId: item.parentId.toString() },
      checkInDate: null as any, // Tarihsiz arama
      checkOutDate: null as any, // Tarihsiz arama
      adults: 1, // Tarihsiz aramalarda sadece 1 yolcu
      children: 0,
      rooms: 1,
      room2Adults: 1,
      room2Children: 0,
      childrenAges: [],
      room2ChildrenAges: []
    });

    this.searchService.searchHotels(searchRequest).subscribe({
      next: (response) => {
        this.navSearchLoading = false;
        if (response.success) {
          this.searchService.setSearchResults(response);
          this.router.navigate(['/hotel-list'], { queryParams: { from: 'nav' } });
        }
      },
      error: (err) => {
        this.navSearchLoading = false;
        this.router.navigate(['/hotel-list']);
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.hotel-dropdown')) {
      this.isHotelDropdownOpen = false;
    }
    if (!target.closest('.user-dropdown')) {
      this.isUserDropdownOpen = false;
    }
  }
}
